import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Package, 
  User, 
  MapPin, 
  CreditCard, 
  Calendar,
  Loader2,
  AlertCircle,
  FileDown,
  ExternalLink,
  Edit,
  Save,
  X,
  Trash2
} from 'lucide-react';
import * as authService from '../../utils/authService';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

const AdminOrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [receiptData, setReceiptData] = useState(null);
  const [designPreviewsLoading, setDesignPreviewsLoading] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editedNotes, setEditedNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const key = await authService.getAdminKey();
        setAdminKey(key);
        fetchOrderDetails();
      } catch (err) {
        console.error("Init error:", err);
        navigate('/admin/login');
      }
    };
    
    init();
  }, [navigate, orderId]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      setDesignPreviewsLoading(true);
      const response = await fetch(
        `/.netlify/functions/get-order?id=${orderId}`, 
        authService.withAuth()
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch order: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Order data from Supabase:', data.order);
      console.log('Order data from S3:', data.orderData);
      setOrder(data.order);
      
      // If we have orderData from S3 (review orders), set it as receiptData
      if (data.orderData) {
        console.log('Setting receiptData from S3 orderData');
        setReceiptData({ orderData: data.orderData });
        setDesignPreviewsLoading(false);
      }
      // Otherwise, fetch from Stripe if there's a session_id (paid orders)
      else if (data.order?.stripe_session_id) {
        fetchReceiptData(data.order.stripe_session_id);
      } else {
        setDesignPreviewsLoading(false);
      }
    } catch (err) {
      console.error('Error fetching order:', err);
      setError(`Failed to load order: ${err.message}`);
      setDesignPreviewsLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchReceiptData = async (sessionId) => {
    try {
      setDesignPreviewsLoading(true);
      const response = await fetch(
        `/.netlify/functions/get-session?session_id=${sessionId}`, 
        authService.withAuth()
      );
      
      if (!response.ok) throw new Error(`Error retrieving receipt data: ${response.status}`);
      
      const data = await response.json();
      console.log('Receipt data from get-session:', data);
      console.log('Cart items:', data?.orderData?.cartItems);
      setReceiptData(data);
    } catch (err) {
      console.error('Failed to fetch receipt data:', err);
    } finally {
      setDesignPreviewsLoading(false);
    }
  };

  const handleEditNotes = () => {
    setEditedNotes(order.admin_notes || '');
    setIsEditingNotes(true);
  };

  const handleCancelEdit = () => {
    setIsEditingNotes(false);
    setEditedNotes('');
  };

  const handleSaveNotes = async () => {
    try {
      setSavingNotes(true);
      
      const response = await fetch('/.netlify/functions/update-admin-notes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.withAuth().headers
        },
        body: JSON.stringify({
          orderId: order.id,
          adminNotes: editedNotes
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update notes: ${response.status}`);
      }

      const result = await response.json();
      
      // Update local order state
      setOrder({ ...order, admin_notes: editedNotes });
      setIsEditingNotes(false);
      setError('');
    } catch (err) {
      console.error('Error saving notes:', err);
      setError(`Kunde inte spara anteckningar: ${err.message}`);
    } finally {
      setSavingNotes(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setUpdatingStatus(true);
      
      const response = await fetch('/.netlify/functions/update-order-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.withAuth().headers
        },
        body: JSON.stringify({
          orderId: order.id,
          newStatus,
          note: `Status ändrad till ${getStatusLabel(newStatus)}`
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update status: ${response.status}`);
      }

      const result = await response.json();
      
      // Update local order state
      setOrder({ ...order, status: newStatus });
      setError('');
      
      // Optionally refresh to get updated history
      setTimeout(() => fetchOrderDetails(), 500);
    } catch (err) {
      console.error('Error updating status:', err);
      setError(`Kunde inte uppdatera status: ${err.message}`);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      
      const response = await fetch(`/.netlify/functions/delete-order?id=${order.id}`, {
        method: 'DELETE',
        headers: authService.withAuth().headers
      });

      if (!response.ok) {
        throw new Error(`Failed to delete order: ${response.status}`);
      }

      // Navigate back to orders list
      navigate('/admin/orders');
    } catch (err) {
      console.error('Error deleting order:', err);
      setError(`Kunde inte radera order: ${err.message}`);
      setShowDeleteDialog(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownloadFiles = async (itemIdOrKey) => {
    try {
      setDownloadingFiles(true);
      
      if (!adminKey) {
        setError('Ingen admin-nyckel tillgänglig. Försök logga in igen.');
        return;
      }

      console.log('Downloading files for:', itemIdOrKey);
      console.log('Admin key length:', adminKey?.length);
      
      // Try to construct the correct S3 path
      // itemIdOrKey could be:
      // 1. Just the UUID (item.id)
      // 2. Full path like "designs/{uuid}"
      // 3. Full path with subdirectory like "designs/{uuid}/decals"
      let s3Key = itemIdOrKey;
      if (!s3Key.startsWith('designs/')) {
        s3Key = `designs/${s3Key}`;
      }
      
      // Fetch the file list from S3
      const url = `/.netlify/functions/s3-proxy?key=${encodeURIComponent(s3Key)}&admin_key=${encodeURIComponent(adminKey)}`;
      console.log('Fetching:', url);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('S3 proxy error:', errorData);
        
        // If we got alternatives, show them in console
        if (errorData.alternatives) {
          console.log('Available files in S3:', errorData.alternatives);
          
          // If there's a suggested URL, try it
          if (errorData.alternatives.suggestedUrl) {
            console.log('Trying suggested URL...');
            window.open(errorData.alternatives.suggestedUrl, '_blank');
            return;
          }
        }
        
        throw new Error(errorData.error || `Failed to fetch files: ${response.status}`);
      }

      const data = await response.json();
      console.log('S3 proxy response:', data);
      
      // If we got a list of files, open each in a new tab
      if (data.files && Array.isArray(data.files)) {
        if (data.files.length === 0) {
          setError('Inga filer hittades för denna design.');
          return;
        }
        
        console.log(`Opening ${data.files.length} files...`);
        data.files.forEach((file, index) => {
          setTimeout(() => {
            console.log('Opening file:', file.filename);
            window.open(file.url, '_blank');
          }, index * 200); // Stagger downloads to avoid popup blocker
        });
      } else if (data.directory && data.files) {
        // Alternative format from s3-proxy
        console.log(`Opening files from ${data.directory}...`);
        data.files.forEach((file, index) => {
          setTimeout(() => {
            window.open(file.url, '_blank');
          }, index * 200);
        });
      } else {
        setError('Inga filer hittades för denna design.');
      }
    } catch (err) {
      console.error('Error downloading files:', err);
      setError(`Kunde inte ladda ner filer: ${err.message}`);
    } finally {
      setDownloadingFiles(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '0 kr';
    return `${(amount / 100).toLocaleString('sv-SE', { minimumFractionDigits: 2 })} kr`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString('sv-SE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-800';
      case 'paid':
        return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-900';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-950 dark:text-yellow-300 dark:border-yellow-900';
      case 'shipped':
        return 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-900';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-900';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-900';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Väntar';
      case 'paid': return 'Betald';
      case 'processing': return 'Under behandling';
      case 'shipped': return 'Skickad';
      case 'completed': return 'Färdigställd';
      case 'cancelled': return 'Avbruten';
      default: return status || 'Okänd';
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex flex-col gap-4 md:gap-8">
          {/* Header Skeleton */}
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/orders')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className="h-8 w-48 bg-muted animate-pulse rounded"></div>
                <div className="h-6 w-16 bg-muted animate-pulse rounded-full"></div>
              </div>
              <div className="h-4 w-32 bg-muted animate-pulse rounded mt-2"></div>
            </div>
          </div>

          {/* Customer & Shipping Skeleton */}
          <div className="grid gap-4 md:grid-cols-2">
            {[1, 2].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-5 bg-muted animate-pulse rounded"></div>
                    <div className="h-5 w-32 bg-muted animate-pulse rounded"></div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-4 w-full bg-muted animate-pulse rounded"></div>
                    <div className="h-4 w-4/5 bg-muted animate-pulse rounded"></div>
                    <div className="h-4 w-3/5 bg-muted animate-pulse rounded"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Payment Information Skeleton */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="h-5 w-5 bg-muted animate-pulse rounded"></div>
                <div className="h-5 w-40 bg-muted animate-pulse rounded"></div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[1, 2].map((i) => (
                  <div key={i} className="flex justify-between items-center py-2">
                    <div className="flex-1">
                      <div className="h-4 w-3/4 bg-muted animate-pulse rounded mb-2"></div>
                      <div className="h-3 w-1/4 bg-muted animate-pulse rounded"></div>
                    </div>
                    <div className="h-4 w-20 bg-muted animate-pulse rounded"></div>
                  </div>
                ))}
                <div className="border-t pt-4 mt-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <div className="h-4 w-24 bg-muted animate-pulse rounded"></div>
                      <div className="h-4 w-20 bg-muted animate-pulse rounded"></div>
                    </div>
                    <div className="flex justify-between">
                      <div className="h-4 w-32 bg-muted animate-pulse rounded"></div>
                      <div className="h-4 w-20 bg-muted animate-pulse rounded"></div>
                    </div>
                    <div className="flex justify-between items-center pt-2">
                      <div className="h-6 w-16 bg-muted animate-pulse rounded"></div>
                      <div className="h-6 w-24 bg-muted animate-pulse rounded"></div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Design Previews Skeleton */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="h-5 w-5 bg-muted animate-pulse rounded"></div>
                <div className="h-5 w-32 bg-muted animate-pulse rounded"></div>
              </div>
              <div className="h-4 w-48 bg-muted animate-pulse rounded mt-2"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="h-6 w-24 bg-muted animate-pulse rounded-full"></div>
                  <div className="h-9 w-32 bg-muted animate-pulse rounded"></div>
                </div>
                <div className="h-[300px] bg-muted animate-pulse rounded-lg"></div>
              </div>
            </CardContent>
          </Card>

          {/* Additional Info Skeleton */}
          <div className="grid gap-4 md:grid-cols-2">
            {[1, 2].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-5 bg-muted animate-pulse rounded"></div>
                    <div className="h-5 w-32 bg-muted animate-pulse rounded"></div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-4 w-full bg-muted animate-pulse rounded"></div>
                    <div className="h-4 w-5/6 bg-muted animate-pulse rounded"></div>
                    <div className="h-4 w-4/6 bg-muted animate-pulse rounded"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error || !order) {
    return (
      <AdminLayout>
        <div className="flex flex-col gap-4 md:gap-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/orders')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h2 className="text-3xl font-bold tracking-tight">Orderdetaljer</h2>
            </div>
          </div>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {error || 'Order kunde inte hittas'}
            </AlertDescription>
          </Alert>
          <Button onClick={() => navigate('/admin/orders')}>
            Tillbaka till ordrar
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="flex flex-col gap-4 md:gap-8">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/orders')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-3xl font-bold tracking-tight">Order #{order.id?.substring(0, 8)}</h2>
                <Badge className={getStatusStyle(order.status)}>
                  {getStatusLabel(order.status)}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {formatDate(order.created_at)}
              </p>
            </div>
            <Button 
              variant="destructive" 
              size="sm"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Radera order
            </Button>
          </div>

          {/* Status Selector Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Orderstatus</CardTitle>
              <CardDescription>Uppdatera orderns aktuella status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Select 
                  value={order.status} 
                  onValueChange={handleStatusChange}
                  disabled={updatingStatus}
                >
                  <SelectTrigger className="w-[250px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-muted-foreground"></div>
                        Väntar
                      </div>
                    </SelectItem>
                    <SelectItem value="paid">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                        Betald
                      </div>
                    </SelectItem>
                    <SelectItem value="processing">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-yellow-500"></div>
                        Under behandling
                      </div>
                    </SelectItem>
                    <SelectItem value="shipped">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-purple-500"></div>
                        Skickad
                      </div>
                    </SelectItem>
                    <SelectItem value="completed">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-green-500"></div>
                        Färdigställd
                      </div>
                    </SelectItem>
                    <SelectItem value="cancelled">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-red-500"></div>
                        Avbruten
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                {updatingStatus && (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                )}
                <div className="text-sm text-muted-foreground">
                  Statusändringar loggas i orderhistoriken
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Fel</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!adminKey && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Ingen admin-nyckel</AlertTitle>
            <AlertDescription>
              Kunde inte hämta admin-nyckeln för filnedladdning. Filnedladdningsknappar kommer inte att fungera.
            </AlertDescription>
          </Alert>
        )}

        {/* Customer & Shipping Info */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Customer */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Kunduppgifter
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div>
                <p className="text-sm font-medium">
                  {order.customer_name || order.customer_info?.name || receiptData?.session?.customer?.name || 'N/A'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {order.customer_email || order.customer_info?.email || receiptData?.session?.customer?.email || 'N/A'}
                </p>
                {(order.customer_phone || order.customer_info?.phone) && (
                  <p className="text-sm text-muted-foreground">
                    {order.customer_phone || order.customer_info.phone}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Shipping */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Leveransadress
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(order.shipping_name || order.shipping_address || receiptData?.session?.shipping) ? (
                <div className="text-sm space-y-1">
                  <p className="font-medium">
                    {order.shipping_name || order.shipping_address?.name || receiptData?.session?.shipping?.name}
                  </p>
                  <p className="text-muted-foreground">
                    {order.shipping_address_line1 || order.shipping_address?.street || receiptData?.session?.shipping?.address?.line1}
                  </p>
                  {(order.shipping_address_line2 || order.shipping_address?.line2 || receiptData?.session?.shipping?.address?.line2) && (
                    <p className="text-muted-foreground">
                      {order.shipping_address_line2 || order.shipping_address?.line2 || receiptData?.session?.shipping?.address?.line2}
                    </p>
                  )}
                  <p className="text-muted-foreground">
                    {order.shipping_postal_code || order.shipping_address?.postal_code || receiptData?.session?.shipping?.address?.postal_code}{' '}
                    {order.shipping_city || order.shipping_address?.city || receiptData?.session?.shipping?.address?.city}
                  </p>
                  <p className="text-muted-foreground">
                    {order.shipping_country || order.shipping_address?.country || receiptData?.session?.shipping?.address?.country}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Ingen leveransadress (granskning pågår)</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Payment Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Betalningsinformation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Line Items */}
              {receiptData?.session?.line_items && receiptData.session.line_items.length > 0 && (
                <div className="space-y-2">
                  {receiptData.session.line_items.map((item, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b last:border-0">
                      <div className="flex-1">
                        <p className="font-medium">{item.description}</p>
                        <p className="text-sm text-muted-foreground">Antal: {item.quantity}</p>
                      </div>
                      <p className="font-medium">{formatCurrency(item.amount_total)}</p>
                    </div>
                  ))}
                </div>
              )}

              <Separator />

              {/* Totals */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Delsumma</span>
                  <span>{formatCurrency(receiptData?.session?.amount_subtotal || order.total_amount * 0.8)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Moms (25%)</span>
                  <span>{formatCurrency(receiptData?.session?.total_details?.amount_tax || order.total_amount * 0.2)}</span>
                </div>
                <Separator />
                <div className="flex justify-between items-center font-bold text-lg">
                  <span>Totalt</span>
                  <span>{formatCurrency(order.total_amount)}</span>
                </div>
              </div>

              {/* Payment Method */}
              <div className="flex items-center gap-2 pt-2">
                <Badge variant="outline">
                  {order.payment_method || receiptData?.session?.payment_method_types?.[0] || 'Kort'}
                </Badge>
                {receiptData?.session?.payment_status && (
                  <Badge variant={receiptData.session.payment_status === 'paid' ? 'default' : 'secondary'}>
                    {receiptData.session.payment_status === 'paid' ? 'Betald' : 'Väntande'}
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Design Previews */}
        {receiptData?.orderData?.cartItems && receiptData.orderData.cartItems.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Kunddesign
              </CardTitle>
              <CardDescription>
                Förhandsvisningar av kundens dekaler
              </CardDescription>
            </CardHeader>
            <CardContent>
              {designPreviewsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-6">
                  {receiptData.orderData.cartItems.map((item, itemIndex) => {
                    // Debug: log item data to console
                    console.log('Cart item data:', {
                      id: item.id,
                      s3Data: item.s3Data,
                      allKeys: Object.keys(item)
                    });
                    
                    return (
                    <div key={itemIndex} className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline">{item.car?.name || 'Design'}</Badge>
                          {item.helfoliering && (
                            <Badge variant="secondary">Helfoliering</Badge>
                          )}
                        </div>
                        {(item.id || item.s3Data?.key) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadFiles(item.s3Data?.key || item.id)}
                            disabled={downloadingFiles || !adminKey}
                          >
                            {downloadingFiles ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Laddar...
                              </>
                            ) : (
                              <>
                                <FileDown className="h-4 w-4 mr-2" />
                                Ladda ner filer
                              </>
                            )}
                          </Button>
                        )}
                      </div>

                      {item.previewImages && Object.keys(item.previewImages).length > 0 ? (() => {
                        // Sort views in the correct order
                        const viewOrder = ['Sida Vänster', 'Sida Höger', 'Fram', 'Bak', 'Topp'];
                        const sortedViews = Object.keys(item.previewImages).sort((a, b) => {
                          const indexA = viewOrder.indexOf(a);
                          const indexB = viewOrder.indexOf(b);
                          // If view not in order array, put it at the end
                          if (indexA === -1) return 1;
                          if (indexB === -1) return -1;
                          return indexA - indexB;
                        });
                        
                        return (
                        <Tabs defaultValue={sortedViews[0]} className="w-full">
                          <TabsList className="w-full justify-start flex-wrap h-auto">
                            {sortedViews.map((view) => (
                              <TabsTrigger key={view} value={view} className="text-xs">
                                {view}
                              </TabsTrigger>
                            ))}
                          </TabsList>
                          {sortedViews.map((view) => {
                            const imageUrl = item.previewImages[view];
                            return (
                            <TabsContent key={view} value={view} className="mt-4">
                              {imageUrl ? (
                                <div className="rounded-lg border bg-muted/50 p-4">
                                  <img
                                    src={imageUrl}
                                    alt={`${item.car?.name || 'Design'} - ${view}`}
                                    className="w-full h-auto object-contain max-h-[400px] mx-auto"
                                  />
                                </div>
                              ) : (
                                <Alert>
                                  <AlertCircle className="h-4 w-4" />
                                  <AlertDescription>
                                    Bilden för denna vy är inte tillgänglig
                                  </AlertDescription>
                                </Alert>
                              )}
                            </TabsContent>
                            );
                          })}
                        </Tabs>
                        );
                      })() : (
                        <Alert>
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>
                            Inga förhandsvisningar tillgängliga
                          </AlertDescription>
                        </Alert>
                      )}

                      {itemIndex < receiptData.orderData.cartItems.length - 1 && (
                        <Separator className="my-6" />
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Order Information */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Customer Notes (Read-only) */}
          <Card>
            <CardHeader>
              <CardTitle>Kundnoteringar</CardTitle>
              <CardDescription>Meddelande från kunden</CardDescription>
            </CardHeader>
            <CardContent>
              {order.customer_notes ? (
                <p className="text-sm whitespace-pre-line">{order.customer_notes}</p>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  Inga kundnoteringar för denna order
                </p>
              )}
            </CardContent>
          </Card>

          {/* Admin Notes (Editable) */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Admin Anteckningar</CardTitle>
                  <CardDescription>Interna anteckningar för ordern</CardDescription>
                </div>
                {!isEditingNotes && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleEditNotes}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Redigera
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isEditingNotes ? (
                <div className="space-y-3">
                  <Textarea
                    value={editedNotes}
                    onChange={(e) => setEditedNotes(e.target.value)}
                    placeholder="Lägg till interna anteckningar..."
                    rows={6}
                    className="resize-none"
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleSaveNotes}
                      disabled={savingNotes}
                    >
                      {savingNotes ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Sparar...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Spara
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelEdit}
                      disabled={savingNotes}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Avbryt
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {order.admin_notes ? (
                    <p className="text-sm whitespace-pre-line">{order.admin_notes}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">
                      Inga admin anteckningar än. Klicka på "Redigera" för att lägga till.
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Order History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Orderhistorik
            </CardTitle>
            <CardDescription>
              Alla statusändringar och händelser för denna order
            </CardDescription>
          </CardHeader>
          <CardContent>
            {order.history && order.history.length > 0 ? (
              <div className="space-y-4">
                {order.history.map((event, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="h-2 w-2 rounded-full bg-primary mt-2"></div>
                      {index < order.history.length - 1 && (
                        <div className="w-px h-full bg-border mt-2"></div>
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex items-center gap-2">
                        <Badge className={getStatusStyle(event.status)}>
                          {getStatusLabel(event.status)}
                        </Badge>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(event.timestamp)}
                        </p>
                      </div>
                      {event.note && (
                        <p className="text-sm text-muted-foreground mt-1">{event.note}</p>
                      )}
                      {event.created_by && (
                        <p className="text-xs text-muted-foreground mt-1">
                          av {event.created_by}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Ingen historik tillgänglig</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Statusändringar kommer att loggas här
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Är du säker?</AlertDialogTitle>
            <AlertDialogDescription>
              Detta kommer permanent radera order #{order.id?.substring(0, 8)} och all relaterad data. 
              Denna åtgärd kan inte ångras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Raderar...
                </>
              ) : (
                'Radera order'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminOrderDetail;
